import { useState } from 'react';
import {
    BodySmallRegular,
    type ColorTokenKey,
    CopyIcon,
    EyeIcon,
    EyeOffIcon,
    Icon,
    isWeb,
    LabelRegular,
    MonoLabelRegular,
    redactPrivate,
    type ShadowStyle,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import type { BankAccountDisplayDetail } from '../../types/BankAccount';
import type { Card } from '../../types/Card';

// Styles need to match Marqeta font details in COPY_PAN / CVV11 / EXP11 so show/hide looks identical
const cardDetailStyles = isWeb
    ? {
          fontFamily: 'Monospace',
          fontSize: '11px',
          lineHeight: 20,
          letterSpacing: 1,
          paddingTop: 1,
      }
    : {
          lineHeight: 20,
          letterSpacing: 1,
          paddingTop: 1,
      };

export const DetailsTileBackgroundColor: ColorTokenKey = '$gray200';

export const useCopiedToast = () => {
    const toast = useToastController();
    const showCopiedToast = () => {
        toast.show('Copied!', {
            duration: 1000,
            icon: (
                <View
                    backgroundColor="$green1000"
                    padding={16}
                    borderRadius={12}
                >
                    <Icon icon={CopyIcon} size={20} color="$white" />
                </View>
            ),
        });
    };

    return { showCopiedToast };
};

export const useToggleSensitiveDataVisibility = () => {
    const [isSensitiveDataVisible, setIsSensitiveDataVisible] = useState(false);
    const toggleSensitiveDataVisibility = () => {
        setIsSensitiveDataVisible((prevState) => !prevState);
    };
    return { isSensitiveDataVisible, toggleSensitiveDataVisibility };
};

export const DetailsTileLabel = ({ text }: { text: string }) => {
    return <LabelRegular color="$gray1000">{text}</LabelRegular>;
};

export const DetailsTileValue = ({ value }: { value: string }) => {
    return (
        <MonoLabelRegular style={cardDetailStyles}>{value}</MonoLabelRegular>
    );
};

/**
 * Optional visual overrides for the tile container. Defaults preserve the
 * existing look so current callers are unaffected; the AI-chat card widget
 * passes a translucent, fully-rounded, shadowed variant to match Figma.
 */
export interface DetailsTileAppearance {
    backgroundColor?: string;
    borderColor?: string;
    borderRadius?: number;
    rowGap?: number;
    shadow?: ShadowStyle;
    fullWidth?: boolean;
    /**
     * Opaque background used by the masked card values to cover the secure
     * `CardData` layer behind them. Must stay opaque so the real PAN never
     * shows through. Defaults to the tile background (`$gray200`); when the
     * tile uses a translucent background, pass a solid color that blends with
     * the rendered surface so the masked cells don't read as boxes.
     */
    maskBackgroundColor?: string;
}

export const DetailsTile = ({
    account,
    card,
    icon,
    name,
    description,
    children,
    isSensitiveDataVisible,
    toggleSensitiveDataVisibility,
    appearance,
}: {
    account?: BankAccountDisplayDetail | null;
    card?: Card | null;
    icon: React.ReactNode;
    name: string;
    description: string;
    children: React.ReactNode;
    isSensitiveDataVisible: boolean;
    toggleSensitiveDataVisibility: () => void;
    appearance?: DetailsTileAppearance;
}) => {
    if (account == null && card == null) {
        return null;
    }

    const {
        backgroundColor = DetailsTileBackgroundColor,
        borderColor = '$gray200',
        borderRadius = 12,
        rowGap = 20,
        shadow,
        fullWidth = false,
    } = appearance ?? {};

    return (
        <YStack
            backgroundColor={backgroundColor}
            borderColor={borderColor}
            borderRadius={borderRadius}
            borderWidth={1}
            paddingVertical={12}
            paddingHorizontal={16}
            gap={rowGap}
            justifyContent="space-between"
            height={fullWidth ? undefined : '100%'}
            width={fullWidth ? '100%' : 300}
            {...(shadow != null ? { style: shadow } : {})}
        >
            <XStack justifyContent="space-between" alignItems="center">
                <XStack gap={16} alignItems="center">
                    <YStack minHeight={42} justifyContent="center">
                        {icon}
                    </YStack>
                    <YStack maxWidth={150}>
                        <BodySmallRegular>{name}</BodySmallRegular>
                        <DetailsTileLabel text={description} />
                    </YStack>
                </XStack>
                <View
                    padding={8}
                    borderRadius={32}
                    backgroundColor="$gray300"
                    borderWidth={1}
                    borderColor="$gray300"
                    onPress={toggleSensitiveDataVisibility}
                    cursor="pointer"
                >
                    <Icon
                        icon={isSensitiveDataVisible ? EyeOffIcon : EyeIcon}
                        color="$gray1200"
                    />
                </View>
            </XStack>
            <XStack
                gap={16}
                justifyContent="space-between"
                height={36}
                {...redactPrivate()}
            >
                {children}
            </XStack>
        </YStack>
    );
};
